import { useMemo, useRef, useState, type FormEvent, type MouseEvent, type ReactNode } from "react";
import { AlertCircle, Loader2 } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import type { OrderItem, OrderRequestType, OrderSupportRequest } from "@/lib/api";
import type { AttachmentUploadProgress } from "@/lib/orderSupportRequestUpload";
import {
  isRetryableSupportRequestError,
  parseSupportRequestError,
  type SupportRequestField,
} from "@/lib/orderSupportRequestError";
import { cn } from "@/lib/utils";
import { CancellationStepper, type CancellationStep } from "../OrderCancellation/CancellationStepper";
import { AttachmentPicker } from "./AttachmentPicker";
import { ItemRequestReview } from "./ItemRequestReview";
import { OrderItemSelector } from "./OrderItemSelector";
import { SupportRequestErrorAlert } from "./SupportRequestErrorAlert";
import {
  buildPayloadItems,
  getItemRequestVariant,
  getSelectableItems,
  isItemsStepValid,
  ITEM_REQUEST_STEPS,
  ITEM_REQUEST_VARIANT_COPY,
  SUPPORT_REQUEST_DESCRIPTION_MAX_LENGTH,
  validateItemsStep,
  type ItemRequestStep,
  type ItemRequestVariant,
} from "./supportRequest";
import { useCreateOrderSupportRequest } from "./useOrderSupportRequests";

type FieldErrors = Partial<Record<"items" | SupportRequestField, string>>;

const FORM_ID = "item-request-form";
const STEP_TITLE_ID = "item-request-step-title";
const DESCRIPTION_ID = "item-request-description";
const DESCRIPTION_ERROR_ID = "item-request-description-error";
/** O contador só aparece perto do limite: com 2000 caracteres, ele seria ruído na maior parte do tempo. */
const DESCRIPTION_COUNTER_THRESHOLD = SUPPORT_REQUEST_DESCRIPTION_MAX_LENGTH - 200;

const STEP_LABELS: Record<ItemRequestStep, string> = {
  items: "Produtos",
  details: "Detalhes",
  review: "Revisão",
};

/**
 * O segundo clique de um duplo clique cairia no botão do passo seguinte, que ocupa o mesmo lugar (ex.: "Continuar"
 * vira "Enviar solicitação"): só o primeiro clique conta. Teclado (Enter/Espaço) chega com `detail` 0.
 */
const ignoreRepeatedClick = (event: MouseEvent) => {
  if (event.detail > 1) event.preventDefault();
};

const pluralize = (count: number, singular: string, plural: string) => `${count} ${count === 1 ? singular : plural}`;

function describeSelection(variant: ItemRequestVariant, count: number): string {
  if (count === 0) return "Nenhum item selecionado.";
  const items =
    variant === "conference"
      ? pluralize(count, "item com diferença", "itens com diferença")
      : pluralize(count, "item selecionado", "itens selecionados");
  return `${items}`;
}

interface ItemRequestDialogProps {
  orderId: string;
  orderNumber: string;
  /** Tipo escolhido no menu; `null` fecha o modal. */
  requestType: OrderRequestType | null;
  items: OrderItem[];
  getItemUsage?: (item: OrderItem) => string | null | undefined;
  onClose: () => void;
  onCreated: (request: OrderSupportRequest) => void;
  /** 409: leva ao acompanhamento da solicitação que já está aberta. */
  onViewExisting: (zendeskTicketId: number | null) => void;
}

/**
 * Solicitação por itens em três passos — produtos (agrupados por série), detalhes (descrição e anexos) e revisão —,
 * para a troca e para a conferência/divergência (`capturesReceivedQuantity`). Vários itens vão num chamado só, cada
 * um com a sua quantidade (na conferência, a recebida); descrição e anexos valem para o chamado inteiro. O cabeçalho
 * é o `title` da API. Causa e solução não são pedidas — a equipe preenche no Zendesk ao finalizar o ticket.
 * Abre num painel lateral com a altura toda da tela: pedidos escolares têm dezenas de itens, que não cabiam no modal
 * centralizado, e o detalhe do pedido continua visível ao lado.
 */
export const ItemRequestDialog = ({
  orderId,
  orderNumber,
  requestType,
  items,
  getItemUsage,
  onClose,
  onCreated,
  onViewExisting,
}: ItemRequestDialogProps) => {
  // Guarda o último tipo pra o cabeçalho não sumir durante a animação de saída, quando `requestType` já é null.
  const [shownType, setShownType] = useState(requestType);
  if (requestType && requestType !== shownType) setShownType(requestType);
  const variant = shownType ? getItemRequestVariant(shownType) : "exchange";
  const copy = ITEM_REQUEST_VARIANT_COPY[variant];

  // Todo o preenchimento fica aqui, fora dos passos: voltar e avançar não perde nada.
  const [step, setStep] = useState<ItemRequestStep>("items");
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [quantities, setQuantities] = useState<Record<string, string>>({});
  const [description, setDescription] = useState("");
  const [attachments, setAttachments] = useState<File[]>([]);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  // Erro de cada item (quantidade inválida ou recusa da API em "Items[n]"), pelo id do item.
  const [itemErrors, setItemErrors] = useState<Record<string, string>>({});
  const [generalErrors, setGeneralErrors] = useState<string[]>([]);
  const [isConfirmingClose, setIsConfirmingClose] = useState(false);

  // Envio direto ao S3: a chave de cada arquivo já enviado fica na sessão do formulário ("Tentar novamente" não sobe
  // de novo) e o botão mostra o progresso dos anexos.
  const uploadedKeys = useRef(new Map<File, string>()).current;
  const [uploadProgress, setUploadProgress] = useState<AttachmentUploadProgress | null>(null);
  const createRequest = useCreateOrderSupportRequest(orderId, { uploadedKeys, onUploadProgress: setUploadProgress });
  const isPending = createRequest.isPending;
  // Um id por formulário (o modal remonta a cada abertura): "Tentar novamente" repete o mesmo envio e o back
  // reaproveita o ticket já criado; uma nova abertura é outra solicitação, mesmo com outra em andamento.
  const [clientRequestId] = useState(() => crypto.randomUUID());
  // `isPending` só chega no próximo render: o ref barra o segundo clique que cair antes disso.
  const submittingRef = useRef(false);
  const formRef = useRef<HTMLFormElement>(null);
  // Ao trocar de passo, o foco vai pro título do passo novo (leitor de tela anuncia onde o cliente está).
  const focusStepTitleRef = useRef(false);

  const selectableItems = useMemo(() => getSelectableItems(items), [items]);
  // Na ordem da lista, para o envio e os erros por posição ("Items[n]") baterem com a tela.
  const selectedItems = selectableItems.filter((item) => selectedIds.includes(item.id));
  const submitError = createRequest.isError ? parseSupportRequestError(createRequest.error) : null;
  const stepIndex = ITEM_REQUEST_STEPS.indexOf(step);
  const isReview = step === "review";

  const goToStep = (next: ItemRequestStep) => {
    focusStepTitleRef.current = true;
    setStep(next);
  };

  const focusStepTitle = (node: HTMLHeadingElement | null) => {
    if (!node || !focusStepTitleRef.current) return;
    focusStepTitleRef.current = false;
    node.focus();
  };

  // Qualquer edição tira o erro do campo e o aviso do último envio (ex.: depois de um 409, mudar os itens).
  const edit = (...fields: (keyof FieldErrors)[]) => {
    setFieldErrors((previous) =>
      fields.some((field) => previous[field])
        ? { ...previous, ...Object.fromEntries(fields.map((field) => [field, undefined])) }
        : previous
    );
    if (createRequest.isError) createRequest.reset();
  };

  const clearItemError = (itemId: string) =>
    setItemErrors((previous) => {
      if (!previous[itemId]) return previous;
      const { [itemId]: _removed, ...rest } = previous;
      return rest;
    });

  const handleToggle = (itemId: string, selected: boolean) => {
    setSelectedIds((previous) => (selected ? [...previous, itemId] : previous.filter((id) => id !== itemId)));
    // Desmarcar descarta a quantidade do item: marcar de novo começa sem quantidade.
    if (!selected) {
      setQuantities(({ [itemId]: _removed, ...rest }) => rest);
    }
    clearItemError(itemId);
    edit("items", "quantity");
  };

  const handleQuantityChange = (itemId: string, value: string) => {
    setQuantities((previous) => ({ ...previous, [itemId]: value }));
    clearItemError(itemId);
    edit("quantity");
  };

  const handleContinue = () => {
    if (step === "items") {
      const validation = validateItemsStep(variant, selectedItems, quantities);
      setFieldErrors((previous) => ({ ...previous, items: validation.error }));
      setItemErrors(validation.itemErrors);
      if (isItemsStepValid(validation)) goToStep("details");
      return;
    }
    // Descrição e anexos validados juntos: o cliente vê de uma vez tudo o que falta no passo.
    const detailsErrors: FieldErrors = {
      description: copy.descriptionRequired && !description.trim() ? "Descreva o que aconteceu." : undefined,
      attachments:
        copy.attachmentsRequired && attachments.length === 0 ? "Anexe pelo menos uma foto ou PDF." : undefined,
    };
    if (detailsErrors.description || detailsErrors.attachments) {
      setFieldErrors((previous) => ({ ...previous, ...detailsErrors }));
      return;
    }
    goToStep("review");
  };

  const submit = () => {
    if (submittingRef.current || isPending || !shownType) return;

    const sentItems = selectedItems;
    setGeneralErrors([]);
    submittingRef.current = true;
    createRequest.mutate(
      {
        requestType: shownType.code,
        clientRequestId,
        description: description.trim(),
        items: buildPayloadItems(variant, sentItems, quantities),
        attachments,
      },
      {
        onSuccess: (created) => onCreated(created),
        onError: (error) => {
          const parsed = parseSupportRequestError(error);
          if (parsed.kind !== "validation") return;
          const nextItemErrors = Object.fromEntries(
            Object.entries(parsed.itemErrors)
              .filter(([index]) => sentItems[Number(index)])
              .map(([index, message]) => [sentItems[Number(index)].id, message])
          );
          setFieldErrors(parsed.fieldErrors);
          setItemErrors(nextItemErrors);
          setGeneralErrors(parsed.generalErrors);
          // Leva ao passo mais cedo com erro; os gerais ficam no topo de qualquer passo.
          if (Object.keys(nextItemErrors).length > 0 || parsed.fieldErrors.quantity) goToStep("items");
          else if (parsed.fieldErrors.description || parsed.fieldErrors.attachments) goToStep("details");
        },
        onSettled: () => {
          submittingRef.current = false;
        },
      }
    );
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (isReview) submit();
    else handleContinue();
  };

  // Com algo preenchido, fechar (X, Esc ou "Cancelar") pergunta antes: a seleção de um pedido grande dá trabalho.
  const hasProgress = selectedIds.length > 0 || description.trim() !== "" || attachments.length > 0;
  const requestClose = () => {
    if (isPending) return;
    if (hasProgress) setIsConfirmingClose(true);
    else onClose();
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) requestClose();
  };

  const blockWhilePending = (event: Event) => {
    if (isPending) event.preventDefault();
  };

  // O Radix focaria o primeiro campo tabulável (a busca); o fluxo começa pela escolha do item.
  const focusFirstItem = (event: Event) => {
    const firstItem = formRef.current?.querySelector<HTMLElement>('[role="checkbox"]');
    if (!firstItem) return;
    event.preventDefault();
    firstItem.focus();
  };

  const stepCaptions: Record<ItemRequestStep, string> = {
    items:
      selectedItems.length === 0
        ? "Produtos e quantidades"
        : pluralize(selectedItems.length, "selecionado", "selecionados"),
    details: copy.detailsCaption,
    review: "Conferir e enviar",
  };
  const steps: CancellationStep[] = ITEM_REQUEST_STEPS.map((key, index) => ({
    label: STEP_LABELS[key],
    caption: stepCaptions[key],
    state: index < stepIndex ? "done" : index === stepIndex ? "current" : "pending",
  }));

  const stepTitle = (title: string, hint?: ReactNode) => (
    <div className="flex flex-col gap-1">
      <h3 id={STEP_TITLE_ID} ref={focusStepTitle} tabIndex={-1} className="text-base font-bold leading-tight outline-none">
        {title}
      </h3>
      {hint && <p className="text-[13px] text-muted-foreground">{hint}</p>}
    </div>
  );

  const submitLabel = submitError && isRetryableSupportRequestError(submitError) ? "Tentar novamente" : "Enviar solicitação";
  const pendingLabel = uploadProgress
    ? `Enviando anexos… ${uploadProgress.current} de ${uploadProgress.total} (${uploadProgress.percent}%)`
    : "Enviando…";

  return (
    <Sheet open={!!requestType} onOpenChange={handleOpenChange}>
      <SheetContent
        side="right"
        className="flex w-full flex-col gap-0 p-0 sm:max-w-[720px]"
        onOpenAutoFocus={focusFirstItem}
        onInteractOutside={blockWhilePending}
        onEscapeKeyDown={blockWhilePending}
      >
        <SheetHeader className="space-y-1 border-b px-6 py-5 pr-12 text-left sm:text-left">
          <SheetTitle className="text-xl font-bold leading-tight">{shownType?.title}</SheetTitle>
          <SheetDescription className="text-sm">Pedido #{orderNumber}</SheetDescription>
        </SheetHeader>

        <div className="border-b px-4 py-4 sm:px-6">
          <CancellationStepper steps={steps} ariaLabel="Etapas da solicitação" className="mx-auto max-w-[520px]" />
        </div>

        <form
          ref={formRef}
          id={FORM_ID}
          onSubmit={handleSubmit}
          noValidate
          className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-6 py-5"
        >
          {generalErrors.length > 0 && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" aria-hidden="true" />
              <AlertDescription>
                {generalErrors.map((message) => (
                  <p key={message}>{message}</p>
                ))}
              </AlertDescription>
            </Alert>
          )}

          {step === "items" && (
            <>
              {stepTitle(copy.itemsTitle, copy.itemsHint)}
              <OrderItemSelector
                items={selectableItems}
                variant={variant}
                selectedIds={selectedIds}
                onToggle={handleToggle}
                quantities={quantities}
                onQuantityChange={handleQuantityChange}
                getItemUsage={getItemUsage}
                error={fieldErrors.items ?? fieldErrors.quantity}
                itemErrors={itemErrors}
                disabled={isPending}
                labelledBy={STEP_TITLE_ID}
              />
            </>
          )}

          {step === "details" && (
            <>
              {stepTitle(copy.detailsTitle)}
              <div className="flex flex-col gap-1.5">
                <div className="flex items-baseline justify-between gap-4">
                  <label htmlFor={DESCRIPTION_ID} className="text-[13px] font-bold text-muted-foreground">
                    {copy.descriptionLabel}
                  </label>
                  <span className="text-xs text-muted-foreground">
                    {copy.descriptionRequired ? "Obrigatório" : "Opcional"}
                  </span>
                </div>
                <Textarea
                  id={DESCRIPTION_ID}
                  value={description}
                  onChange={(e) => {
                    setDescription(e.target.value.slice(0, SUPPORT_REQUEST_DESCRIPTION_MAX_LENGTH));
                    edit("description");
                  }}
                  maxLength={SUPPORT_REQUEST_DESCRIPTION_MAX_LENGTH}
                  placeholder={copy.descriptionPlaceholder}
                  disabled={isPending}
                  aria-required={copy.descriptionRequired}
                  aria-invalid={!!fieldErrors.description}
                  aria-describedby={fieldErrors.description ? DESCRIPTION_ERROR_ID : undefined}
                  className={cn(
                    "min-h-[110px] resize-y text-[14.5px]",
                    fieldErrors.description && "border-destructive"
                  )}
                />
                {(fieldErrors.description || description.length >= DESCRIPTION_COUNTER_THRESHOLD) && (
                  <div className="flex items-start justify-between gap-4">
                    <span>
                      {fieldErrors.description && (
                        <span id={DESCRIPTION_ERROR_ID} role="alert" className="text-[13px] font-medium text-destructive">
                          {fieldErrors.description}
                        </span>
                      )}
                    </span>
                    {description.length >= DESCRIPTION_COUNTER_THRESHOLD && (
                      <span className="shrink-0 text-xs text-muted-foreground">
                        {SUPPORT_REQUEST_DESCRIPTION_MAX_LENGTH - description.length} caracteres restantes
                      </span>
                    )}
                  </div>
                )}
              </div>

              <AttachmentPicker
                files={attachments}
                onChange={(files) => {
                  setAttachments(files);
                  edit("attachments");
                }}
                error={fieldErrors.attachments}
                required={copy.attachmentsRequired}
                disabled={isPending}
              />
            </>
          )}

          {isReview && (
            <>
              {stepTitle("Revise antes de enviar")}
              <ItemRequestReview
                variant={variant}
                items={selectedItems}
                quantities={quantities}
                description={description}
                attachments={attachments}
                onEdit={goToStep}
                disabled={isPending}
              />
              {submitError && <SupportRequestErrorAlert error={submitError} />}
            </>
          )}
        </form>

        <SheetFooter className="items-center gap-2 border-t px-6 py-4 sm:justify-between sm:space-x-0">
          <p className="text-[13px] text-muted-foreground" aria-live="polite">
            {describeSelection(variant, selectedItems.length)}
          </p>
          <div className="flex w-full flex-col-reverse gap-2 sm:w-auto sm:flex-row">
            {step === "items" ? (
              <Button type="button" variant="outline" onClick={requestClose} disabled={isPending}>
                Cancelar
              </Button>
            ) : (
              <Button
                type="button"
                variant="outline"
                onClick={() => goToStep(ITEM_REQUEST_STEPS[stepIndex - 1])}
                disabled={isPending}
              >
                Voltar
              </Button>
            )}
            {isReview && submitError?.kind === "alreadyOpen" ? (
              <Button type="button" onClick={() => onViewExisting(submitError.zendeskTicketId)}>
                Ver solicitação
              </Button>
            ) : (
              <Button
                type="submit"
                form={FORM_ID}
                onClick={ignoreRepeatedClick}
                disabled={isPending || selectableItems.length === 0}
                className="font-bold"
              >
                {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
                {!isReview ? "Continuar" : isPending ? pendingLabel : submitLabel}
              </Button>
            )}
          </div>
        </SheetFooter>

        <AlertDialog open={isConfirmingClose} onOpenChange={setIsConfirmingClose}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Descartar solicitação?</AlertDialogTitle>
              <AlertDialogDescription>
                Os produtos marcados e o que você preencheu até aqui serão perdidos.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Continuar preenchendo</AlertDialogCancel>
              <AlertDialogAction
                onClick={onClose}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Descartar
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </SheetContent>
    </Sheet>
  );
};
